import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { timingCounts } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { CatalogSong } from '../../../../../redux/api/fymAPI.types';
import { MasterAudioStore } from '../player/masterAudio';
import { useMasterPlayer } from '../player/useMasterPlayer';
import { LyricResyncNoteView } from './LyricResyncNoteView';
import { LyricTimingEditorView } from './LyricTimingEditorView';
import { LyricTimingStateCard } from './LyricTimingStateCard';
import { lyricTimingMessages } from './lyricTimingMessages';
import { useLyricResync } from './useLyricResync';
import { useLyricTimingEditor } from './useLyricTimingEditor';

interface LyricTimingSectionProps {
  accountId: number;
  itemUuid: string;
  song: CatalogSong;
  /** "Add the lyrics" of the no-lyrics state. With none, the state has no button. */
  onAddLyrics?: () => void;
  /** The lyric editor page: the list shows every line. */
  isPage?: boolean;
  /** For tests. The app has one master player. */
  playerStore?: MasterAudioStore;
  /** The demo artist: no Re-sync, no "Set times by hand", no − / + and no T (design song-page f7). */
  readOnly?: boolean;
  /** The action of the no-master state (design song-page d4: "Open Song files"). */
  noMasterAction?: { label: string; onClick: () => void };
}

/** The sentence of a failed sync (design s8b). The other reasons use the general sentence. */
function failMessage(reason: string | null) {
  if (reason === 'noVocals') {
    return lyricTimingMessages.failNoVocals;
  }
  if (reason === 'noMatch') {
    return lyricTimingMessages.failNoMatch;
  }
  return lyricTimingMessages.failGeneral;
}

/**
 * The lyric timing of a song (designs s8, s8b; spec §5.8, §5.9), under the lyrics of the song files
 * sheet and on the lyric editor page. The states: no master, no lyrics, not synced yet, a failed
 * sync (Re-sync, "Set times by hand"), and the editor (syncing, synced, failed by hand).
 * `catalog.song.updated` reads the timing again; with no socket the editor polls while it syncs.
 */
const LyricTimingSectionComponent: React.FC<LyricTimingSectionProps> = ({
  accountId,
  itemUuid,
  song,
  onAddLyrics,
  isPage = false,
  playerStore,
  readOnly = false,
  noMasterAction,
}) => {
  const intl = useIntl();
  const { master } = song;
  const hasLyrics = song.lyrics !== null && song.lyrics.trim() !== '';
  const canSync = master !== null && master.status !== 'failed' && hasLyrics;
  const canPlay = master !== null && (master.canPlay ?? master.status === 'ready');
  const [isByHand, setIsByHand] = useState(false);

  const player = useMasterPlayer({ accountId, itemUuid, store: playerStore });
  const editor = useLyricTimingEditor({
    accountId,
    itemUuid,
    // The analysis length first: the server checks the order of the lines against it.
    durationSec: master?.durationSec ?? player.durationSec ?? null,
    enabled: canSync,
  });
  const { timing } = editor;
  const editedCount = useMemo(
    () => (timing === null || timing === undefined ? 0 : timingCounts(timing).editedCount),
    [timing]
  );
  const resync = useLyricResync({
    accountId,
    itemUuid,
    timing,
    editedCount,
    discardPending: editor.discardPending,
  });

  let body: React.ReactNode;
  // The editor shows the Re-sync note under its own head; a state card shows it under its buttons.
  let isEditorShown = false;
  if (master === null) {
    body = (
      <LyricTimingStateCard
        tone="off"
        pill={intl.formatMessage(lyricTimingMessages.pillNoMaster)}
        body={intl.formatMessage(lyricTimingMessages.noMaster)}
        actions={noMasterAction === undefined || readOnly ? undefined : [noMasterAction]}
      />
    );
  } else if (!hasLyrics) {
    body = (
      <LyricTimingStateCard
        tone="off"
        pill={intl.formatMessage(lyricTimingMessages.pillNoLyrics)}
        body={intl.formatMessage(lyricTimingMessages.noLyrics)}
        actions={
          onAddLyrics === undefined
            ? undefined
            : [{ label: intl.formatMessage(lyricTimingMessages.addLyrics), onClick: onAddLyrics }]
        }
      />
    );
  } else if (master.status === 'failed') {
    body = (
      <LyricTimingStateCard
        tone="off"
        pill={intl.formatMessage(lyricTimingMessages.pillNotSynced)}
        body={intl.formatMessage(lyricTimingMessages.masterFailed)}
      />
    );
  } else if (editor.error !== undefined) {
    body = <ErrorView error={editor.error} retry={editor.retry} />;
  } else if (editor.isLoading || timing === undefined) {
    body = <LoadingView />;
  } else if (timing === null) {
    const isProcessing = master.status === 'processing';
    body = (
      <LyricTimingStateCard
        tone="off"
        pill={intl.formatMessage(lyricTimingMessages.pillNotSynced)}
        body={intl.formatMessage(isProcessing ? lyricTimingMessages.masterNotReady : lyricTimingMessages.notSynced)}
        actions={
          isProcessing || readOnly
            ? undefined
            : [
                {
                  label: intl.formatMessage(lyricTimingMessages.syncNow),
                  onClick: resync.request,
                  disabled: resync.isBusy,
                },
              ]
        }
      />
    );
  } else if (timing.status === 'failed' && !isByHand) {
    body = (
      <LyricTimingStateCard
        tone="bad"
        pill={intl.formatMessage(lyricTimingMessages.pillFailed)}
        body={`${intl.formatMessage(failMessage(timing.failReason))} ${intl.formatMessage(lyricTimingMessages.failHint)}`}
        actions={
          readOnly
            ? undefined
            : [
                {
                  label: intl.formatMessage(lyricTimingMessages.resync),
                  onClick: resync.request,
                  disabled: resync.isBusy,
                },
                { label: intl.formatMessage(lyricTimingMessages.byHand), onClick: () => setIsByHand(true) },
              ]
        }
      />
    );
  } else {
    isEditorShown = true;
    body = (
      <LyricTimingEditorView
        timing={timing}
        player={player}
        canPlay={canPlay}
        isPage={isPage}
        readOnly={readOnly}
        isSaving={editor.isSaving}
        isResyncing={resync.isBusy}
        resyncNote={resync.note}
        onResync={resync.request}
        setStart={editor.setStart}
        nudge={editor.nudge}
      />
    );
  }

  return (
    <section aria-label={intl.formatMessage(lyricTimingMessages.title)} data-testid="lyric-timing-section">
      {body}
      {isEditorShown ? null : <LyricResyncNoteView note={resync.note} />}
      <ConfirmDialog
        isOpen={resync.isConfirmOpen}
        tone="danger"
        title={intl.formatMessage(lyricTimingMessages.confirmTitle)}
        description={intl.formatMessage(lyricTimingMessages.confirmBody, { count: editedCount })}
        confirmLabel={intl.formatMessage(lyricTimingMessages.confirmAction)}
        isBusy={resync.isBusy}
        onConfirm={resync.confirm}
        onCancel={resync.cancel}
        testId="lyric-resync-confirm"
      />
    </section>
  );
};

export const LyricTimingSection = TypedMemo(LyricTimingSectionComponent);
